"use client";

import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { SingleProduct } from "@/__generated__/sdk";
import { SVariantDto } from "@/__generated__/graphql";
import { ProductPageContext, ProductPageVariantsMode } from "./ProductPageContext";
import CartModeGuideDialog, { PricingModeGuideRequest } from "../components/CartModeGuideDialog";
import { useCartProductItems } from "@/redux/features/cart/hooks";

export type SelectedAttributes = Record<string, string>;

export type AttributeValueState = "enabled" | "disabled" | "incompatible";

export type AttributeValue = {
  value: string;
  state: AttributeValueState;
};

export type AttributeGroup = {
  name: string;
  values: AttributeValue[];
};

/* -------------------------------------------------------------------------- */
/*                            VARIANT MATCHING LOGIC                           */
/* -------------------------------------------------------------------------- */

function variantMatchesAttrs(variant: SVariantDto, selectedAttrs: SelectedAttributes): boolean {
  if (!variant.attrs?.length) {
    // attribute-less variants participate when no attrs are required / selected is empty-compatible
    return Object.keys(selectedAttrs).length === 0;
  }

  const everyVariantAttrOk = variant.attrs.every((attr) => {
    const sel = selectedAttrs[attr.name];
    return sel === undefined || sel === attr.value;
  });
  if (!everyVariantAttrOk) return false;

  // every selected attribute must exist on the variant with the same value
  return Object.entries(selectedAttrs).every(([name, value]) =>
    variant.attrs!.some((attr) => attr.name === name && attr.value === value),
  );
}

/** First matching variant (legacy single-seller selection). */
export function findMatchingVariant(variants: SVariantDto[], selectedAttrs: SelectedAttributes): SVariantDto | undefined {
  return findMatchingVariants(variants, selectedAttrs)[0];
}

/**
 * All in-stock warehouse variants that match the selected attributes.
 * Used for Technolife-like multi-seller rows on the PDP.
 */
export function findMatchingVariants(variants: SVariantDto[], selectedAttrs: SelectedAttributes): SVariantDto[] {
  if (!variants?.length) return [];

  const hasSelection = Object.keys(selectedAttrs).length > 0;
  if (!hasSelection) return [];

  return variants.filter((variant) => variant.hasStock && variantMatchesAttrs(variant, selectedAttrs));
}

/* -------------------------------------------------------------------------- */
/*                       ADVANCED ATTRIBUTE GROUP LOGIC                       */
/* -------------------------------------------------------------------------- */

function computeGlobalAvailability(variants: SVariantDto[]) {
  const groups: Record<string, Map<string, boolean>> = {};

  for (const variant of variants) {
    if (!variant?.attrs) continue;

    for (const attr of variant.attrs) {
      if (!groups[attr.name]) {
        groups[attr.name] = new Map();
      }

      const prev = groups[attr.name].get(attr.value) ?? false;
      groups[attr.name].set(attr.value, prev || variant.hasStock);
    }
  }

  return groups; // map: groupName → value → hasStock
}

function isValueCompatible(variants: SVariantDto[], selectedAttrs: SelectedAttributes, groupName: string, value: string) {
  // Simulate selection if the user chooses this value
  const simulated = { ...selectedAttrs, [groupName]: value };

  return variants.some((variant) => {
    if (!variant.hasStock) return false;
    if (!variant.attrs) return false;

    return variant.attrs.every((attr) => {
      // skip conditions where user hasn’t selected anything for this attribute
      if (simulated[attr.name] === undefined) return true;
      return simulated[attr.name] === attr.value;
    });
  });
}

// export function extractAttributeGroupsAdvanced(variants: SVariantDto[], selectedAttrs: SelectedAttributes): AttributeGroup[] {
//   const globalStockMap = computeGlobalAvailability(variants);

//   return Object.entries(globalStockMap).map(([groupName, valuesMap]) => ({
//     name: groupName,
//     values: Array.from(valuesMap.entries()).map(([value, globalStock]) => {
//       // If no stock globally → completely disabled
//       if (!globalStock) return { value, state: "disabled" as AttributeValueState };

//       // If globally available but no compatible variant → incompatible
//       const compatible = isValueCompatible(variants, selectedAttrs, groupName, value);

//       return {
//         value,
//         state: compatible ? "enabled" : ("incompatible" as AttributeValueState),
//       };
//     }),
//   }));
// }



export function extractAttributeGroupsAdvanced(
  variants: SVariantDto[],
  selectedAttrs: SelectedAttributes
): AttributeGroup[] {
  const globalStockMap = computeGlobalAvailability(variants);

  return Object.entries(globalStockMap).map(([groupName, valuesMap]) => {

    const values: AttributeValue[] = Array.from(valuesMap.entries()).map(
      ([value, globalStock]) => {
        if (!globalStock) {
          return { value, state: "disabled" as AttributeValueState };
        }

        const compatible = isValueCompatible(
          variants,
          selectedAttrs,
          groupName,
          value
        );

        return {
          value,
          state: compatible ? "enabled" : "incompatible",
        };
      }
    );

    // Out-of-stock last; otherwise stable Persian alpha.
    values.sort((a, b) => {
      const aOos = a.state === "disabled" ? 1 : 0;
      const bOos = b.state === "disabled" ? 1 : 0;
      if (aOos !== bOos) return aOos - bOos;
      return a.value.localeCompare(b.value, "fa", { sensitivity: "base" });
    });

    return {
      name: groupName,
      values,
    };
  });
}



function findBestVariant(variants: SVariantDto[]) {
  const available = variants.filter(v => v.hasStock);

  if (!available.length) return null;

  return available.reduce((prev, cur) => {
    const prevPrice = prev.offer?.price ?? prev.price;
    const curPrice = cur.offer?.price ?? cur.price;

    return curPrice < prevPrice ? cur : prev;
  });
}
export function getMissingAttributes(
  attributeGroups: AttributeGroup[],
  selectedAttrs: SelectedAttributes
): string[] {
  return attributeGroups
    .map((g) => g.name)
    .filter((name) => !selectedAttrs[name]);
}
export function handleAddToCart(
  mode: ProductPageVariantsMode,
  attributeGroups: AttributeGroup[],
  selectedAttrs: SelectedAttributes,
  onSuccess: () => void,
  onError?: (missingAttrs: string[]) => void
) {
  if (mode === "withAttrs") {
    const missing = getMissingAttributes(attributeGroups, selectedAttrs);

    if (missing.length > 0) {
      onError?.(missing);
      return;
    }
  }

  onSuccess();
}

export function buildMissingAttributesError(
  missingAttrs: string[]
): string | null {
  if (!missingAttrs.length) return null;

  if (missingAttrs.length === 1) {
    return `اول ${missingAttrs[0]} را انتخاب کنید`;
  }

  return `اول ${missingAttrs.join(" و ")} را انتخاب کنید`;
}

export type ProductSearchParams = Record<string, string | string[] | undefined>;

function firstQueryValue(value: string | string[] | undefined): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

function queryValueCandidates(raw: string): string[] {
  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw).trim();
    if (decoded && decoded !== raw) candidates.push(decoded);
  } catch {
    // A broken escape sequence is not a variant value.
  }
  return candidates;
}

function variantAttrRecord(variant: SVariantDto | null | undefined): SelectedAttributes {
  if (!variant?.attrs?.length) return {};
  return variant.attrs.reduce((acc, attr) => {
    acc[attr.name] = attr.value;
    return acc;
  }, {} as SelectedAttributes);
}

/** attr.name → query param key (`attr.key`, fallback to name). */
function attributeQueryKeyByName(variants: SVariantDto[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const variant of variants) {
    for (const attr of variant.attrs ?? []) {
      if (!map.has(attr.name)) map.set(attr.name, attr.key || attr.name);
    }
  }
  return map;
}

function writeSelectedAttrsToQuery(variants: SVariantDto[], selected: SelectedAttributes, params: URLSearchParams) {
  const nameToKey = attributeQueryKeyByName(variants);
  for (const [name, key] of nameToKey) {
    params.delete(name);
    params.delete(key);
  }
  for (const [name, value] of Object.entries(selected)) {
    params.set(nameToKey.get(name) ?? name, value);
  }
}

/**
 * Digikala-style: if the new pick conflicts with other selected attrs,
 * jump to the cheapest in-stock combo that includes this pick.
 */
export function resolveSelectionAfterPick(
  variants: SVariantDto[],
  prev: SelectedAttributes,
  name: string,
  value: string,
): SelectedAttributes {
  const next = { ...prev, [name]: value };
  if (findMatchingVariants(variants, next).length > 0) return next;

  const candidates = variants.filter(
    (variant) => variant.hasStock && variant.attrs?.some((attr) => attr.name === name && attr.value === value),
  );
  if (!candidates.length) return { [name]: value };

  const best = candidates.reduce((prevVariant, cur) => {
    const prevPrice = prevVariant.offer?.price ?? prevVariant.price;
    const curPrice = cur.offer?.price ?? cur.price;
    return curPrice < prevPrice ? cur : prevVariant;
  });
  return variantAttrRecord(best);
}

/**
 * Reads variant attributes from the URL (`key=value`; legacy `name=value` still works).
 * Unknown keys/names and values that are not on this product are ignored.
 */
export function readSelectedAttrsFromQuery(variants: SVariantDto[], searchParams?: ProductSearchParams): SelectedAttributes {
  if (!searchParams) return {};

  const byName = new Map<string, { key?: string; values: Set<string> }>();
  for (const variant of variants) {
    for (const attr of variant.attrs ?? []) {
      const entry = byName.get(attr.name) ?? { key: attr.key, values: new Set<string>() };
      entry.values.add(attr.value);
      if (attr.key) entry.key = attr.key;
      byName.set(attr.name, entry);
    }
  }

  const selected: SelectedAttributes = {};
  for (const [name, entry] of byName) {
    const raw =
      (entry.key ? firstQueryValue(searchParams[entry.key]) : undefined) ?? firstQueryValue(searchParams[name]);
    if (!raw) continue;
    const match = queryValueCandidates(raw).find((candidate) => entry.values.has(candidate));
    if (match) selected[name] = match;
  }
  return selected;
}

/* -------------------------------------------------------------------------- */
/*                            PROVIDER COMPONENT                               */
/* -------------------------------------------------------------------------- */

type Props = PropsWithChildren<{ product: SingleProduct; searchParams?: ProductSearchParams }>;

function replaceUrlQuery(params: URLSearchParams) {
  const query = params.toString();
  const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(window.history.state, "", next);
}

export default function ProductPageProvider({ children, product, searchParams }: Props) {
  const [tempError, setTempError] = useState<string | null>(null);
  const [promptedAttrs, setPromptedAttrs] = useState<string[]>([]);
  const [pricingModeGuide, setPricingModeGuide] = useState<PricingModeGuideRequest | null>(null);
  const variants: SVariantDto[] = useMemo(() => product.variants ?? [], [product]);
  const productId = product.id;


  const mode: ProductPageVariantsMode = useMemo(() => {
    const withAttrs = variants.filter(v => v.attrs?.length).length

    if (withAttrs === 0) return 'withOutAttrs'
    if (withAttrs === variants.length) return 'withAttrs'
    return 'mixed'
  }, [variants])
  
  

  useEffect(() => {
    if (!tempError) return;

    const timer = setTimeout(() => {
      setTempError(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [tempError]);
  const userCartItemsForThisProduct = useCartProductItems(productId);
  const activeCartItem = useMemo(
    () => userCartItemsForThisProduct.find((item) => item.quantity > 0) ?? null,
    [userCartItemsForThisProduct],
  );
  const cartVariantId = activeCartItem ? Number(activeCartItem.variantId) : null;
  const appliedCartSelection = useRef(false);

  const anyVariantAvailableToSell = useMemo(() => variants.some((v) => v.hasStock), [variants]);

  const defaultVariantBeforeUserSelectOne = useMemo(() => {
    if (!anyVariantAvailableToSell) return null;

    const withStock = variants.filter((v) => v.hasStock);
    if (!withStock.length) return null;

    return withStock.reduce((min, v) => {
      const price = v.offer?.price ?? v.price;
      const minPrice = min.offer?.price ?? min.price;
      return price < minPrice ? v : min;
    });
  }, [variants, anyVariantAvailableToSell]);
  
  const defaultSelectedVariant = useMemo(() => {
    if (mode === 'withOutAttrs') {
      return findBestVariant(variants)
    }
    return null
  }, [mode])

  const [selectedAttrs, setSelectedAttrs] = useState<SelectedAttributes>(() => {
    // Cart wins so the buy box opens on the seller already in the basket.
    if (activeCartItem) {
      const productVariant = variants.find((variant) => variant.id === Number(activeCartItem.variantId));
      const cartAttrs = variantAttrRecord(productVariant ?? (activeCartItem.variant as SVariantDto | undefined));
      if (Object.keys(cartAttrs).length > 0) return cartAttrs;
    }

    const fromQuery = readSelectedAttrsFromQuery(variants, searchParams);
    if (Object.keys(fromQuery).length > 0) return fromQuery;

    if (mode === 'withOutAttrs' && defaultSelectedVariant) {
      return variantAttrRecord(defaultSelectedVariant);
    }

    return {};
  });

  // Cart often hydrates after first paint; apply once so attrs/buy box catch up.
  useEffect(() => {
    if (appliedCartSelection.current || !activeCartItem) return;
    appliedCartSelection.current = true;

    const productVariant = variants.find((variant) => variant.id === Number(activeCartItem.variantId));
    const cartAttrs = variantAttrRecord(productVariant ?? (activeCartItem.variant as SVariantDto | undefined));
    if (Object.keys(cartAttrs).length === 0) return;

    setSelectedAttrs(cartAttrs);

    const params = new URLSearchParams(window.location.search);
    const before = params.toString();
    writeSelectedAttrsToQuery(variants, cartAttrs, params);
    const after = params.toString();
    if (after !== before) replaceUrlQuery(params);
  }, [activeCartItem, variants]);

  const matchingVariants = useMemo(() => {
    const preferCart = (list: SVariantDto[]) => {
      if (cartVariantId == null) return list;
      const index = list.findIndex((variant) => variant.id === cartVariantId);
      if (index <= 0) return list;
      const next = [...list];
      const [hit] = next.splice(index, 1);
      return [hit, ...next];
    };

    if (mode === "withOutAttrs") {
      const inStock = variants.filter((v) => v.hasStock);
      if (cartVariantId == null || inStock.some((variant) => variant.id === cartVariantId)) {
        return preferCart(inStock);
      }
      const cartVariant = variants.find((variant) => variant.id === cartVariantId);
      return preferCart(cartVariant ? [cartVariant, ...inStock] : inStock);
    }

    const attrs = Object.keys(selectedAttrs).length > 0 ? selectedAttrs : variantAttrRecord(defaultVariantBeforeUserSelectOne);
    // shortcut: do not inject cart when attrs point at another combo — buy box must follow the pick
    return preferCart(findMatchingVariants(variants, attrs));
  }, [mode, variants, selectedAttrs, defaultVariantBeforeUserSelectOne, cartVariantId]);

  const selectedVariant = useMemo(
    () => matchingVariants[0] ?? findMatchingVariant(variants, selectedAttrs),
    [matchingVariants, variants, selectedAttrs],
  );

  const selectAttribute = (name: string, value: string) => {
    const next = resolveSelectionAfterPick(variants, selectedAttrs, name, value);
    setSelectedAttrs(next);
    setPromptedAttrs((prev) => prev.filter((item) => !next[item]));

    const params = new URLSearchParams(window.location.search);
    writeSelectedAttrsToQuery(variants, next, params);
    replaceUrlQuery(params);
  };

  const promptForAttributes = () => {
    const missing = getMissingAttributes(attributeGroups, selectedAttrs);
    if (!missing.length) return;
    setPromptedAttrs(missing);
  };

  const onAddToCart = (callback: () => void): string[] | null => {
    if (mode !== "withOutAttrs") {
      const missing = getMissingAttributes(attributeGroups, selectedAttrs);

      if (missing.length) {
        setPromptedAttrs(missing);
        return missing;
      }
    }

    callback();
    return null;
  };


  const attributeGroups = useMemo(() => extractAttributeGroupsAdvanced(variants, selectedAttrs), [variants, selectedAttrs]);

  useEffect(() => {
    if (!promptedAttrs.length) return;
    const target = document.getElementById("variant-pickers");
    if (!target) return;

    target.classList.remove("scroll-target-glow");
    void target.offsetWidth;
    target.classList.add("scroll-target-glow");
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [promptedAttrs]);

  const variantToShow = selectedVariant ?? defaultSelectedVariant ?? defaultVariantBeforeUserSelectOne;
  const hasOffer = Boolean(variantToShow?.offer);
  const hasAnyOffer = variants.some((a) => a.offer);
  return (
    <ProductPageContext.Provider
      value={{
        mode,
        anyVariantAvailableToSell,
        productId,
        selectedVariant,
        matchingVariants,
        selectAttribute,
        selectedAttrs,
        attributeGroups,
        hasOffer,
        variants,
        hasAnyOffer,
        variantToShow,
        onAddToCart,
        tempError,
        promptedAttrs,
        promptForAttributes,
        openPricingModeGuide: setPricingModeGuide,
      }}
    >
      {children}
      <CartModeGuideDialog
        request={pricingModeGuide}
        productTitle={product.title}
        imageSrc={product.images?.find((image) => image.isMain)?.address ?? product.images?.[0]?.address}
        onClose={() => setPricingModeGuide(null)}
      />
    </ProductPageContext.Provider>
  );
}



